import { useEffect, useRef, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { chain } from "lodash"
import { Paper, IconButton, Badge } from "@mui/material"
import classNames from "classnames"
import { FilterList } from "@mui/icons-material"
import pluralize from "pluralize"
import ToolbarHeader from "components/securities-table/toolbar-header"
import { MAX_SECURITIES_DOWNLOAD } from "helpers/securities-search"
import Table from "components/securities-table/table"
import SecuritiesSearchParameters from "modules/securities-search-parameters"
import WatchlistFacets from "modules/watchlist-facets"
import DateSelector from "components/date-picker"
import ColumnSelector from "components/securities-table/column-selector"
import { intFormatter } from "helpers/formatters/numbers"
import Tooltip from "components/material/tooltip"
import { DATE } from "helpers/shapes"
import "./style.scss"

export default function Watchlist({
  watchlist,
  columns,
  page,
  setPage,
  openSecurityProfile,
  openFactorProfile,
  openCompositeSecurity,
  status,
  setSort,
  modelStartDate,
  modelEndDate,
  selectedDate,
  unmodeledDates,
  selectedSecurities,
  selectSecurity,
  currentPageSelectionStatus,
  selectAllSecurities,
  toolbarActive,
  watchlistId,
  toggleFilters,
  filtersVisible,
  filterSubset,
  toggleWatchlistsMenu,
  watchlistsMenuVisible,
  columnSelectorOpen,
  toggleColumnSelector,
  selectedColumns,
  searchColumn,
  columnSearches,
  filteredColumnsSelector,
  modelId,
  account,
  count,
  isOpPro,
  accountCurrency,
  isHistoricalFactorSensitivityAvailable,
  updateFilters,
  setDate,
  removeFromWatchlist,
  resetSelectedSecurities,
  watchlistTotalSecurities,
  watchlistFacetsTotalSecurities,
  setSelectedColumns,
  sort,
  download,
  filter,
}) {
  const filterButtonRef = useRef(null)

  useEffect(() => {
    return () => {
      resetSelectedSecurities()
    }
  }, [resetSelectedSecurities])

  const filtersCount = useMemo(() => {
    return Object.keys(filterSubset ?? {}).length
  }, [filterSubset])

  const onDeleteSelection = () => {
    removeFromWatchlist(watchlistId, selectedSecurities)
  }

  const getSecuritiesTooltip = useCallback(() => {
    if (!filterSubset && watchlistTotalSecurities !== watchlistFacetsTotalSecurities) {
      const watchlistUnmappedCount = watchlistTotalSecurities - watchlistFacetsTotalSecurities
      return `${intFormatter(watchlistTotalSecurities)} ${pluralize("security", watchlistTotalSecurities)} in watchlist, ${watchlistUnmappedCount} unmapped ${pluralize("security", watchlistUnmappedCount)} not displayed.`
    }
  }, [filterSubset, watchlistTotalSecurities, watchlistFacetsTotalSecurities])

  const setSelectedColumnsFun = (columns) => {
    setSelectedColumns(columns, modelId)
  }

  const onDownload = useCallback(() => {
    const apiSort = sort.getter(sort.id, sort.direction)
    const selectedColumns = chain(columns)
      .map(column => column.columns)
      .flatten()
      .value()

    download(account, modelId, filter, apiSort, selectedColumns, selectedDate)
  }, [account, modelId, filter, columns, sort, selectedDate, download])

  return <div className="modules_watchlist">
    <Paper elevation={1} className="watchlist-header-wrapper">
      <Paper square elevation={0} className="watchlist-date-wrapper">
        <div className="watchlist-date">
          <DateSelector
            earliestDate={modelStartDate}
            latestDate={modelEndDate}
            selectedDate={selectedDate}
            exclusionMap={unmodeledDates}
            onChange={setDate}
          />
        </div>
        <Tooltip title="Filter Positions">
          <IconButton
            ref={filterButtonRef}
            onClick={toggleFilters}
            className={classNames("watchlist-filters-toggle filters-toggle", { active: filtersVisible })}
            size="large"
          >
            <Badge overlap="rectangular" color="primary" invisible={filtersVisible} badgeContent={filtersCount}>
              <FilterList />
            </Badge>
          </IconButton>
        </Tooltip>
      </Paper>
    </Paper>
    <div className="results">
      <WatchlistFacets selectedDate={selectedDate} modelId={modelId} watchlist={watchlist} />
      <Paper elevation={1} className="data-table">
        <ColumnSelector
          open={columnSelectorOpen}
          toggle={toggleColumnSelector}
          selectedColumns={selectedColumns}
          setSort={setSort}
          setSelectedColumns={setSelectedColumnsFun}
          columnSearches={columnSearches}
          searchColumn={searchColumn}
          filteredColumnsSelector={filteredColumnsSelector}
        />
        <ToolbarHeader
          heapCreateWatchlistId="watchlist-create-watchlist"
          heapAddToWatchlistId="watchlist-apply-to-watchlist"
          active={toolbarActive}
          onColumnSelectorToggle={toggleColumnSelector}
          watchlistsMenuVisible={watchlistsMenuVisible}
          toggleWatchlistsMenu={toggleWatchlistsMenu}
          watchlistToFilter={watchlistId}
          onDeleteSelection={onDeleteSelection}
          selectedSecuritiesTotal={selectedSecurities.total}
          selectedSecurities={selectedSecurities.securities}
          title={`${intFormatter(watchlist.count)} ${pluralize("security", watchlist.count)}`}
          titleTooltip={getSecuritiesTooltip()}
          download={onDownload}
          count={count}
          modelId={modelId}
          maxSecuritiesDownload={isOpPro ? MAX_SECURITIES_DOWNLOAD.pro : MAX_SECURITIES_DOWNLOAD.enterprise}
        />
        <Table
          columns={columns}
          currentPageSelectionStatus={currentPageSelectionStatus}
          selectAllSecurities={selectAllSecurities}
          count={watchlist.count}
          setPage={setPage}
          status={status}
          page={page}
          selectedSecurities={selectedSecurities.securities}
          selectSecurity={selectSecurity}
          setSort={setSort}
          openSecurityProfile={openSecurityProfile}
          openFactorProfile={openFactorProfile}
          openCompositeSecurity={(security) => openCompositeSecurity(account, security, false, modelId)}
          foundSecurities={watchlist.securities}
        />
      </Paper>
    </div>
    {filtersVisible && <SecuritiesSearchParameters
      heapAddFilterId="watchlist-filter-add"
      className={classNames({ visible: filtersVisible })}
      elevation={0}
      universeDisabled
      actionsDisabled
      filter={filterSubset}
      account={account}
      modelId={modelId}
      selectedDate={selectedDate}
      currency={accountCurrency}
      isHistoricalFactorSensitivityAvailable={isHistoricalFactorSensitivityAvailable}
      toggleFilters={toggleFilters}
      filtersVisible={filtersVisible}
      anchorEl={filterButtonRef.current}
      updateFilters={updateFilters}
    />}
  </div>
}

Watchlist.propTypes = {
  watchlist: PropTypes.object.isRequired,
  columns: PropTypes.array,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  status: PropTypes.string,
  modelStartDate: PropTypes.string.isRequired,
  modelEndDate: PropTypes.string.isRequired,
  selectedDate: DATE.isRequired,
  unmodeledDates: PropTypes.object.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  selectedSecurities: PropTypes.object.isRequired,
  selectSecurity: PropTypes.func.isRequired,
  currentPageSelectionStatus: PropTypes.string,
  selectAllSecurities: PropTypes.func.isRequired,
  removeFromWatchlist: PropTypes.func.isRequired,
  watchlistId: PropTypes.string.isRequired,
  resetSelectedSecurities: PropTypes.func.isRequired,
  toolbarActive: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  filterSubset: PropTypes.object,
  toggleWatchlistsMenu: PropTypes.func,
  watchlistsMenuVisible: PropTypes.bool,
  columnSelectorOpen: PropTypes.bool.isRequired,
  toggleColumnSelector: PropTypes.func.isRequired,
  selectedColumns: PropTypes.object.isRequired,
  setSelectedColumns: PropTypes.func.isRequired,
  searchColumn: PropTypes.func.isRequired,
  columnSearches: PropTypes.object.isRequired,
  filteredColumnsSelector: PropTypes.array.isRequired,
  modelId: PropTypes.string.isRequired,
  watchlistTotalSecurities: PropTypes.number,
  watchlistFacetsTotalSecurities: PropTypes.number,
  account: PropTypes.string.isRequired,
  preferredSelectedColumns: PropTypes.objectOf(PropTypes.bool),
  download: PropTypes.func,
  filter: PropTypes.object,
  sort: PropTypes.object,
  count: PropTypes.number,
  isOpPro: PropTypes.bool.isRequired,
  accountCurrency: PropTypes.string,
  isHistoricalFactorSensitivityAvailable: PropTypes.bool,
  updateFilters: PropTypes.func,
}
